import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { ButtonLayout, QuoteType } from "@/lib/utils";
import { useFacility } from "@/hooks/useFacility";

export const useTriggerButtonVisibility = () => {
    const { unitGroup } = useUnitItemContext();
    const {
        buttonLayout,
        disableRental,
        disableReservation,
        disableSoftReservation,
    } = useDisplayOptionContext();
    const { facility } = useFacility(unitGroup.representative_unit.facility_id);
    const hasMultipleSubGroups =
        Array.isArray(unitGroup.sub_groups) && unitGroup.sub_groups.length > 1;
    const hasAvailableUnits = unitGroup.available > 0;
    const reservationWindowAllowed = unitGroup.reservation_allowed;

    /*
     * Rentals are enabled if:
     * - plugin doesn't disable rental
     * - not reserve-only
     * - not click-to-call
     */
    const canRent = !disableRental;

    /*
     * Reservations are enabled if:
     * - plugin doesn't disable reservation
     * - not click-to-call
     * - (window allows) OR (reserve-only overrides window=0)
     */
    const canReserve = !disableReservation && reservationWindowAllowed;

    /*
     * Soft reservations are enabled if:
     * - plugin doesn't disable soft reservations
     * - not click-to-call
     */
    const canSoftReserve =
        !disableSoftReservation &&
        facility?.settings.quote_type !== QuoteType.CALL;

    const shouldShowSelectButton = () => {
        if (!canRent && !canReserve) {
            return false;
        }
        /*
         * If there are no available units, the only button that will show
         * will be Quote or Call for Availability, never Select.
         */
        if (!hasAvailableUnits) {
            return false;
        }

        /*
         * If the button layout is "separate", we never show Select unless
         * there are multiple subgroups.
         */
        if (buttonLayout === ButtonLayout.SEPARATE && !hasMultipleSubGroups) {
            return false;
        }

        /*
         * If we reach here, we know:
         * - There are available units
         * - The reservation type is not click-to-call
         * - The button layout is either "combined" or there are multiple subgroups
         */
        if (hasMultipleSubGroups) {
            return true;
        }

        /*
         * As long as one of rental or reservation is available,
         * we show the Select button.
         */
        return canRent || canReserve;
    };

    return {
        hasMultipleSubGroups,
        hasAvailableUnits,
        canRent,
        canReserve,
        canSoftReserve,
        shouldShowSelectButton,
    };
};
